import { ControllerInterface } from "csi/action/Controller";
import { C8Node } from "csi/typings";
import { action, observable, makeObservable, computed } from "mobx";
import { createContext, useContext } from "react";
import { ReactionStore } from "csi/utility/mobxUtility";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";

interface hierarchyInformation {

    /** the mapping of hierarchy folder urls to the urls and names of their children */
    hierarchyStructure: Object;

    /** the names of each hierarchy subtype, ordered by level */
    hierarchyTypeNames: string[];
}

export default class ProductHierarchySelectStore extends ReactionStore {
    @observable private _folderValues: string[] = [];

    /** the mapping of hierarchy folder urls to the names and urls of their children */
    readonly folderStructure: object = {};

    /** the names of each hierarchy subtype, ordered by level */
    readonly folderTypeNames: string[];

    /** the top level hierarchy node */
    readonly productHierarchy: C8Node;

    /** the empty value error text */
    readonly emptyErrorMessage: string;

    /** whether or not any select has been focused and then blurred */
    focused: boolean = false;

    /** setting the value to that of the leaf folder */
    onChange: Function;

    /**
     * function to set the new value at the i-th product hierarchy, clear its children and set the leafFolder
     * @param newValue the new value of the corresponding select
     * @param i the level of the product hierarchy to set the new value
     */
    @action setFolderValues = (newValue: string, i: number): void => {
    	if ((i < this.folderValues.length) && (newValue in this.folderStructure || newValue === "")) {
    		this.folderValues[i] = newValue;
    	}

    	for (let x = i + 1; x < this.folderValues.length; x += 1) {
    		if (!this.folderValues[x - 1] || this.folderStructure[this.folderValues[x - 1]].length !== 2) {
    			this.folderValues[x] = "";
    		} else {
    			this.folderValues[x] = this.folderStructure[this.folderValues[x - 1]][1].id;
    		}
    	}

    	if (this.onChange) {
    		this.onChange(this.folderValues[this.folderValues.length - 1]);
    	}
    };

    /** the array of values corresponding to each hierarchy level */
    get folderValues(): string[] {
    	return this._folderValues;
    }

    /** 
     * function to get the items for all product hierarchy selects
     * @returns list of select items, where the index corresponds to the level of the list
     */
    @computed get folderItems(): ComponentItem[][] {
    	return this.folderValues.map((value, i) => {
    		if (i === 0) {
    			return [
    				{
    					id: this.productHierarchy.$URL,
    					name: this.productHierarchy.$Name,
    				},
    			];
    		} else {
    			return this.folderStructure[this.folderValues[i - 1]];
    		}
    	}) as ComponentItem[][];
    }
    

    setFocused = (newValue: boolean): void => {
    	this.focused = newValue;
    };

    initializeFolderValues(): void {
    	this._folderValues = new Array(this.folderTypeNames.length).fill("");
    	this.setFolderValues(this.productHierarchy.$URL, 0);
    }

    constructor(actionController: ControllerInterface, locale: ILocaleProvider, hierarchyInformation: hierarchyInformation, onChange?: Function) {
    	super();
    	makeObservable(this);

    	const cache = actionController.getContext().cache;
    	const node: C8Node = actionController.getContext().getNode();
    	const { hierarchyStructure, hierarchyTypeNames } = hierarchyInformation;

    	this.productHierarchy = cache.getPathNode(node, "Child:ProductHierarchyDetails/Child:ProductHierarchy");
    	this.folderTypeNames = hierarchyTypeNames;
    	this.folderStructure = hierarchyStructure;

    	this.emptyErrorMessage = locale.resource("RequiredValue");
    	this.onChange = onChange;

    	this.initializeFolderValues();
    }
}

export const ProductHierarchySelectStoreContext = createContext<ProductHierarchySelectStore>(undefined);
export const useProductHierarchySelectStore = (): ProductHierarchySelectStore => useContext(ProductHierarchySelectStoreContext);